import { snapshot as authSnapshot } from "@internal/auth";
import { getDb, type PlannerDoc } from "@/lib/db";
import {
  DEFAULT_SETTINGS_DATA,
  type FoodClarification,
  type Ingredient,
  MEAL_METRICS,
  type MealLogSummaryConfig,
  type Recipe,
  SETTINGS_TYPE,
  type SettingsData,
  settingsId,
  type WeeklyPointSummaryConfig,
  type WidgetConfig,
} from "@/lib/schema";
import { refTargetsWidget } from "@/lib/widget-refs";

function currentUserId(): string {
  const user = authSnapshot().user;
  if (!user) throw new Error("Not signed in");
  return user.id;
}

export function parseFoodClarifications(value: unknown): FoodClarification[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (f): f is FoodClarification =>
      !!f &&
      typeof (f as FoodClarification).id === "string" &&
      typeof (f as FoodClarification).text === "string" &&
      (f as FoodClarification).text.trim().length > 0,
  );
}

function isValidIngredient(value: unknown): value is Ingredient {
  const ing = value as Partial<Ingredient> | null;
  return (
    !!ing &&
    typeof ing.name === "string" &&
    typeof ing.grams === "number" &&
    Number.isFinite(ing.grams) &&
    ing.grams >= 0 &&
    MEAL_METRICS.every((metric) => {
      const amount = ing[metric];
      return (
        typeof amount === "number" && Number.isFinite(amount) && amount >= 0
      );
    })
  );
}

export function parseRecipes(value: unknown): Recipe[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (r): r is Recipe =>
      !!r &&
      typeof (r as Recipe).id === "string" &&
      typeof (r as Recipe).name === "string" &&
      (r as Recipe).name.trim().length > 0 &&
      Array.isArray((r as Recipe).ingredients) &&
      (r as Recipe).ingredients.length > 0 &&
      (r as Recipe).ingredients.every(isValidIngredient),
  );
}

function rowToData(doc: PlannerDoc | null): SettingsData {
  if (!doc) return { ...DEFAULT_SETTINGS_DATA, widgets: [] };
  const stored = (doc.data ?? {}) as Partial<SettingsData>;
  const foodClarifications = parseFoodClarifications(stored.foodClarifications);
  const recipes = parseRecipes(stored.recipes);
  return {
    widgets: Array.isArray(stored.widgets) ? stored.widgets : [],
    ...(foodClarifications.length > 0 ? { foodClarifications } : {}),
    ...(recipes.length > 0 ? { recipes } : {}),
  };
}

const writeQueues = new Map<string, Promise<unknown>>();
function enqueue<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const prev = writeQueues.get(key) ?? Promise.resolve();
  const next = prev.then(fn, fn);
  writeQueues.set(
    key,
    next.catch(() => {}),
  );
  return next;
}

async function modifySettings(
  modify: (current: SettingsData) => SettingsData,
): Promise<void> {
  const userId = currentUserId();
  const db = await getDb();
  const id = settingsId(userId);
  await enqueue(id, async () => {
    const existing = await db.daily_planner.findOne(id).exec();
    const current = rowToData(
      existing ? (existing.toJSON() as PlannerDoc) : null,
    );
    const next = modify(current);
    if (existing) {
      await existing.patch({
        data: next as unknown as Record<string, unknown>,
      });
    } else {
      await db.daily_planner.insert({
        id,
        type: SETTINGS_TYPE,
        user_id: userId,
        data: next as unknown as Record<string, unknown>,
      });
    }
  });
}

export async function addWidget(config: WidgetConfig): Promise<void> {
  await modifySettings((s) => ({ ...s, widgets: [...s.widgets, config] }));
}

export async function updateWidget(
  id: string,
  patch: Partial<WidgetConfig>,
): Promise<void> {
  await modifySettings((s) => ({
    ...s,
    widgets: s.widgets.map((w) =>
      w.id === id ? ({ ...w, ...patch } as WidgetConfig) : w,
    ),
  }));
}

export async function deleteWidget(id: string): Promise<void> {
  await modifySettings((s) => ({
    ...s,
    widgets: s.widgets.filter((w) => w.id !== id),
  }));
}

/**
 * What deleting a widget does to another widget that references it. Two kinds
 * refer to others and they break differently: a summary row loses one of
 * several references (and is dropped when it loses its last), while a
 * Matoversikt has exactly one source and is left unconfigured.
 */
type CascadeEffect =
  | {
      kind: "weekly-point-summary";
      rows: Array<{ rowId: string; rowName: string; willBeDropped: boolean }>;
    }
  | { kind: "meal-log-summary" };

export interface CascadeImpact {
  widgetId: string;
  affected: Array<{
    widgetId: string;
    widgetLabel: string;
    effect: CascadeEffect;
  }>;
}

export function findCascadeImpact(
  widgets: WidgetConfig[],
  targetId: string,
): CascadeImpact {
  const affected: CascadeImpact["affected"] = [];
  for (const w of widgets) {
    if (w.kind === "weekly-point-summary") {
      const ps = w as WeeklyPointSummaryConfig;
      const rows = ps.rows
        .filter((r) => r.widgetIds.some((x) => refTargetsWidget(x, targetId)))
        .map((r) => ({
          rowId: r.id,
          rowName: r.name,
          willBeDropped: r.widgetIds.length === 1,
        }));
      if (rows.length > 0) {
        affected.push({
          widgetId: ps.id,
          widgetLabel: ps.label,
          effect: { kind: "weekly-point-summary", rows },
        });
      }
      continue;
    }
    if (w.kind === "meal-log-summary") {
      const summary = w as MealLogSummaryConfig;
      if (summary.mealLogId !== targetId) continue;
      affected.push({
        widgetId: summary.id,
        widgetLabel: summary.label,
        effect: { kind: "meal-log-summary" },
      });
    }
  }
  return { widgetId: targetId, affected };
}

export async function deleteWidgetWithCascade(id: string): Promise<void> {
  await modifySettings((s) => {
    const remaining = s.widgets.filter((w) => w.id !== id);
    const next = remaining.map((w) => {
      if (w.kind === "weekly-point-summary") {
        const ps = w as WeeklyPointSummaryConfig;
        const rows = ps.rows
          // Scrubs part references too, not just the bare-UUID one.
          .map((r) => ({
            ...r,
            widgetIds: r.widgetIds.filter((x) => !refTargetsWidget(x, id)),
          }))
          .filter((r) => r.widgetIds.length > 0);
        return { ...ps, rows };
      }
      if (w.kind === "meal-log-summary") {
        const summary = w as MealLogSummaryConfig;
        if (summary.mealLogId !== id) return summary;
        // Cleared rather than deleted: the widget keeps its Energiforbruk and
        // calculator inputs, and its editor forces a new pick because validate
        // gates Save on the reference.
        return { ...summary, mealLogId: "" };
      }
      return w;
    });
    return { ...s, widgets: next };
  });
}

export async function replaceWidgets(widgets: WidgetConfig[]): Promise<void> {
  await modifySettings((s) => ({ ...s, widgets }));
}

export async function replaceFoodClarifications(
  clarifications: FoodClarification[],
): Promise<void> {
  await modifySettings((s) => {
    const { foodClarifications: _previous, ...rest } = s;
    return {
      ...rest,
      ...(clarifications.length > 0
        ? { foodClarifications: clarifications }
        : {}),
    };
  });
}

export async function replaceRecipes(recipes: Recipe[]): Promise<void> {
  await modifySettings((s) => {
    const { recipes: _previous, ...rest } = s;
    return { ...rest, ...(recipes.length > 0 ? { recipes } : {}) };
  });
}

// Save-as-recipe: replace by id when the caller resolved a name match,
// append otherwise.
export async function upsertRecipe(recipe: Recipe): Promise<void> {
  await modifySettings((s) => {
    const current = s.recipes ?? [];
    const exists = current.some((r) => r.id === recipe.id);
    const next = exists
      ? current.map((r) => (r.id === recipe.id ? recipe : r))
      : [...current, recipe];
    return { ...s, recipes: next };
  });
}
